// src/common/components/cartModal/components/deliveryTimeSelector/DeliveryTimeSelector.tsx
import React, { useState, useEffect } from "react";
import { MdArrowForward } from "react-icons/md";
import { useTheme } from "../../../../hooks/useTheme";
import styles from "./DeliveryTimeSelector.module.scss";

interface DeliveryTimeSelectorProps {
  displayText: string;
  pickerVisible: boolean;
  minimumDate: Date | null;
  onPress: () => void;
  onClose: () => void;
  onSelect: (date?: Date) => void;
  isTimeAvailable: (date: Date) => boolean;
}

const isToday = (date: Date): boolean => {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const formatDateTime = (date: Date): string => {
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatLocalDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const DeliveryTimeSelector: React.FC<DeliveryTimeSelectorProps> = ({
  displayText,
  pickerVisible,
  minimumDate,
  onPress,
  onClose,
  onSelect,
  isTimeAvailable,
}) => {
  const { theme } = useTheme();
  const [selectedDate, setSelectedDate] = useState<Date | null>(minimumDate);
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => {
    setSelectedDate(minimumDate);
  }, [minimumDate]);

  useEffect(() => {
    if (!selectedDate) {
      setIsValid(false);
      return;
    }
    setIsValid(isTimeAvailable(selectedDate));
  }, [selectedDate, isTimeAvailable]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    setSelectedDate(date);
  };

  const handleConfirm = () => {
    if (isValid && selectedDate) {
      onSelect(selectedDate);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedDate(minimumDate);
    setIsValid(minimumDate ? isTimeAvailable(minimumDate) : false);
    onClose();
  };

  const showTomorrowWarning = minimumDate && !isToday(minimumDate);

  return (
    <div className={styles.container}>
      <span className={styles.label} style={{ color: theme.textSecondary }}>
        ⏰ Время получения
      </span>

      {showTomorrowWarning && (
        <p
          className={styles.errorText}
          style={{ color: theme.warning ?? theme.danger, marginBottom: 8 }}
        >
          ⚠️ Заказ на сегодня невозможен!
        </p>
      )}

      <button className={styles.selector} onClick={onPress}>
        <span className={styles.text} style={{ color: theme.text }}>
          {displayText}
        </span>
        <MdArrowForward size={18} color={theme.textSecondary} />
      </button>

      {pickerVisible && minimumDate && selectedDate && (
        <div className={styles.pickerOverlay} onClick={onClose}>
          <div
            className={styles.pickerModal}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className={styles.pickerTitle} style={{ color: theme.text }}>
              Выберите время получения
            </h4>

            <input
              type="datetime-local"
              className={styles.pickerInput}
              value={formatLocalDateTime(selectedDate)}
              min={formatLocalDateTime(minimumDate)}
              onChange={handleDateChange}
              style={{
                backgroundColor: theme.card,
                borderColor: theme.border,
                color: theme.text,
              }}
            />

            {!isValid && (
              <p className={styles.errorText} style={{ color: theme.danger }}>
                ⚠️ В это время мы не работаем. Выберите другое время!
              </p>
            )}

            <div className={styles.pickerActions}>
              <button className={styles.cancelButton} onClick={handleCancel}>
                Отмена
              </button>
              <button
                className={`${styles.confirmButton} ${!isValid ? styles.disabled : ""}`}
                onClick={handleConfirm}
                disabled={!isValid}
                style={{
                  opacity: isValid ? 1 : 0.5,
                  cursor: isValid ? "pointer" : "not-allowed",
                }}
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};